import React, { Component } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5stock from "@amcharts/amcharts5/stock";
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark";

class AmChartsCandleChart extends Component {
  constructor(props) {
    super(props);
    this.chartDivId = "chartdiv-" + Math.random().toString(36).slice(2);
    this.controlsDivId = "controls-" + Math.random().toString(36).slice(2);
    this.userInteracted = false;
    this.isHovering = false;
  }

  componentDidMount() {
    const { candleData = [], showVolume = false, symbol = "" } = this.props;
    if (!candleData.length) return;

    const root = am5.Root.new(this.chartDivId);
    this.root = root;
    root.setThemes([am5themes_Dark.new(root)]);
    root.numberFormatter.set("numberFormat", "#,###.####");

    const stockChart = root.container.children.push(am5stock.StockChart.new(root, {}));

    const mainPanel = stockChart.panels.push(
      am5stock.StockPanel.new(root, {
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        height: am5.percent(showVolume ? 80 : 100),
        layout: root.verticalLayout
      })
    );

    mainPanel.plotContainer.setAll({
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
    });

    const dateAxis = mainPanel.xAxes.push(
      am5xy.GaplessDateAxis.new(root, {
        baseInterval: { timeUnit: "minute", count: 1 },
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 35 })
      })
    );
    const valueAxis = mainPanel.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 35,
          maxDeviation: 1,
          pan: "zoom",
        }),
        // This line is the key fix — prevents auto-refit when zoomed in
        strictMinMaxSelection: true
      })
    );

    valueAxis.get("renderer").labels.template.setAll({
      paddingRight: 0,
    });

    const markUserInteraction = () => {
      this.userInteracted = true;
    };
    dateAxis.events.on("panstarted", markUserInteraction);
    dateAxis.events.on("zoomstarted", markUserInteraction);
    mainPanel.events.on("panstarted", markUserInteraction);
    mainPanel.events.on("zoomstarted", markUserInteraction);

    const candleSeries = mainPanel.series.push(
      am5xy.CandlestickSeries.new(root, {
        name: "Price",
        xAxis: dateAxis,
        yAxis: valueAxis,
        valueXField: "time",
        openValueYField: "open",
        highValueYField: "high",
        lowValueYField: "low",
        valueYField: "close",
        calculateAggregates: true,
        tooltip: am5.Tooltip.new(root, {
          labelText:
            "[bold]{valueX.formatDate('MMM dd HH:mm')}[/]\n" +
            "O: {open}\nH: {high}\nL: {low}\nC: {close}"
        })
      })
    );

    candleSeries.columns.template.adapters.add("fill", (_, target) => {
      const d = target.dataItem?.dataContext;
      return d?.close >= d?.open ? am5.color("#029C81") : am5.color("#ff4d4f");
    });

    candleSeries.columns.template.adapters.add("stroke", (_, target) => {
      const d = target.dataItem?.dataContext;
      return d?.close >= d?.open ? am5.color("#029C81") : am5.color("#ff4d4f");
    });

    candleSeries.columns.template.set("width", am5.percent(80));

    stockChart.set("stockSeries", candleSeries);

    const priceLine = mainPanel.series.push(
      am5xy.LineSeries.new(root, {
        xAxis: dateAxis,
        yAxis: valueAxis,
        valueXField: "time",
        valueYField: "close",
        stroke: am5.color("#ffffff"),
        strokeDasharray: [6, 4],
        strokeWidth: 2
      })
    );

    const priceAxisItem = valueAxis.makeDataItem({
      value: candleData[candleData.length - 1]?.close || 0
    });

    const priceRange = valueAxis.createAxisRange(priceAxisItem);
    priceRange.get("grid").set("visible", false);

    const priceLabel = priceRange.get("label");
    priceLabel.setAll({
      fill: am5.color("#ffffff"),
      background: am5.RoundedRectangle.new(root, {
        fill: am5.color("#029C81"),
        cornerRadiusTL: 6,
        cornerRadiusTR: 6,
        cornerRadiusBL: 6,
        cornerRadiusBR: 6
      }),
      paddingLeft: 2,
      paddingRight: 2,
      paddingTop: 4,
      paddingBottom: 4,
      centerY: am5.p50
    });

    const cursor = mainPanel.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        xAxis: dateAxis,
        yAxis: valueAxis,
        snapToSeries: [candleSeries],
        snapToSeriesBy: "x!",
        behavior: "none"
      })
    );

    cursor.lineX.setAll({
      visible: true,
      stroke: am5.color("#ffffff"),
      strokeWidth: 1,
      strokeOpacity: 1,
      strokeDasharray: [6, 6],
      layer: 1000
    });

    cursor.lineY.setAll({
      visible: true,
      stroke: am5.color("#ffffff"),
      strokeWidth: 1,
      strokeOpacity: 1,
      strokeDasharray: [6, 6],
      layer: 1000
    });

    cursor.events.on("cursormoved", () => {
      this.isHovering = true;
      const di = candleSeries.get("tooltipDataItem");
      if (di) {
        const value = di.get("valueY");
        if (isFinite(value)) {
          priceAxisItem.set("value", value);
          priceLabel.set("text", `${symbol} ${value.toFixed(2)}`);
        }
      }
    });

    cursor.events.on("cursorhidden", () => {
      this.isHovering = false;
      cursor.lineX.set("visible", false);
      cursor.lineY.set("visible", false);
      this.updateLatestPrice();
    });

    mainPanel.plotContainer.events.on("pointerover", () => {
      cursor.lineX.set("visible", true);
      cursor.lineY.set("visible", true);
    });

    const scrollbar = mainPanel.set("scrollbarX", am5xy.XYChartScrollbar.new(root, { height: 50 }));

    const sbDateAxis = scrollbar.chart.xAxes.push(
      am5xy.GaplessDateAxis.new(root, {
        baseInterval: { timeUnit: "minute", count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {})
      })
    );

    const sbValueAxis = scrollbar.chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {})
      })
    );

    const sbSeries = scrollbar.chart.series.push(
      am5xy.LineSeries.new(root, {
        xAxis: sbDateAxis,
        yAxis: sbValueAxis,
        valueXField: "time",
        valueYField: "close"
      })
    );

    am5stock.StockToolbar.new(root, {
      container: document.getElementById(this.controlsDivId),
      stockChart,
      controls: [
        am5stock.IndicatorControl.new(root, { stockChart }),
        am5stock.DrawingControl.new(root, { stockChart }),
        am5stock.DateRangeSelector.new(root, { stockChart }),
        am5stock.PeriodSelector.new(root, { stockChart }),
        am5stock.ResetControl.new(root, { stockChart }),
        am5stock.SettingsControl.new(root, { stockChart }),
      ]
    });

    const data = candleData.map(d => ({ ...d, time: d.time * 1000 }));
    candleSeries.data.setAll(data);
    sbSeries.data.setAll(data);
    priceLine.data.setAll([data[data.length - 1]]);

    this.refsChart = {
      cursor,
      candleSeries,
      sbSeries,
      priceLine,
      dateAxis,
      valueAxis,
      priceAxisItem,
      priceLabel,
      symbol: symbol || ""
    };

    cursor.lineX.set("visible", false);
    cursor.lineY.set("visible", false);
    this.updateLatestPrice();
  }

  updateLatestPrice = () => {
    const { candleSeries, priceAxisItem, priceLabel, symbol } = this.refsChart || {};
    if (!candleSeries || candleSeries.dataItems.length === 0) return;

    const lastItem = candleSeries.dataItems[candleSeries.dataItems.length - 1];
    const lastValue = lastItem.get("valueY");

    if (isFinite(lastValue)) {
      priceAxisItem.set("value", lastValue);
      priceLabel.set("text", `${symbol} ${lastValue.toFixed(2)}`);
    }
  };

  componentDidUpdate(prevProps) {
    if (!this.refsChart || !this.props.candleData?.length) return;

    const { candleSeries, sbSeries, priceLine, dateAxis, valueAxis } = this.refsChart;

    const data = this.props.candleData.map(d => ({ ...d, time: d.time * 1000 }));
    const last = data[data.length - 1];

    const existing = candleSeries.data.values;
    const lastExisting = existing[existing.length - 1];

    if (lastExisting && lastExisting.time === last.time) {
      candleSeries.data.setIndex(existing.length - 1, last);
      sbSeries.data.setIndex(existing.length - 1, last);
    } else {
      candleSeries.data.push(last);
      sbSeries.data.push(last);
    }

    priceLine.data.setAll([last]);

    if (!this.isHovering) {
      this.updateLatestPrice();
    }

    // Auto-follow X-axis only if user never interacted
    if (!this.userInteracted && candleSeries.data.length > 0) {
      const visibleCount = Math.min(60, candleSeries.data.length);
      const startTime = candleSeries.data.values[candleSeries.data.length - visibleCount].time;
      const endTime = last.time + 60_000;

      dateAxis.zoomToDates(new Date(startTime), new Date(endTime), false, true);
    }
  }

  componentWillUnmount() {
    this.root?.dispose();
  }

  render() {
    return (
      <div style={{ height: "100%", width: "100%", position: "relative" }} className="flex flex-col max-md:justify-start mt-8">
        <div id={this.controlsDivId} style={{ padding: "0px 15px 0px 15px" }} />
        <div id={this.chartDivId} style={{ height: "100%", width: "100%" }} className="max-md:!h-[78%] -mt-14 " />
        
      </div>
    );
  }
}

export default AmChartsCandleChart;